import React, { memo, useEffect, useMemo, useState } from "react";
import {
  calculateDelayPenaltyDays,
  calculateDueCompletion,
} from "../utils/dueCompletion.ts";
import { tokens } from "./theme";
import {
  Badge,
  Button,
  Card,
  FormGroupDivider,
  FormRow,
  Input,
  Select,
  Table,
  Td,
  Textarea,
  Th,
} from "./ui";

const t = tokens;
const WEEKDAYS = [
  { value: 1, label: "M", title: "Monday" },
  { value: 2, label: "T", title: "Tuesday" },
  { value: 3, label: "W", title: "Wednesday" },
  { value: 4, label: "T", title: "Thursday" },
  { value: 5, label: "F", title: "Friday" },
  { value: 6, label: "S", title: "Saturday" },
  { value: 0, label: "S", title: "Sunday" },
];

const newId = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

function numberOrZero(value) {
  const next = Number(value);
  return Number.isFinite(next) ? next : 0;
}

function toLines(items) {
  return (items || []).filter(Boolean).join("\n");
}

function fromLines(value) {
  return String(value || "")
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function normaliseSettings(currentProject = {}) {
  const existing = currentProject.dueCompletionSettings || {};
  return {
    commencementDate: existing.commencementDate || currentProject.startDate || "",
    durationValue: existing.durationValue ?? currentProject.contractDurationDays ?? "",
    durationUnit: existing.durationUnit || "calendarDays",
    calculationMode: existing.calculationMode || "gccCalendarDays",
    workingDaysPerWeek: existing.workingDaysPerWeek ?? 5,
    countCommencementDate: existing.countCommencementDate === true,
    nonWorkingWeekdays: Array.isArray(existing.nonWorkingWeekdays) ? existing.nonWorkingWeekdays : [0, 6],
    publicHolidays: Array.isArray(existing.publicHolidays) ? existing.publicHolidays : [],
    specialNonWorkingPeriods: Array.isArray(existing.specialNonWorkingPeriods)
      ? existing.specialNonWorkingPeriods
      : [],
    sections: Array.isArray(existing.sections) ? existing.sections : [],
  };
}

function normaliseAdjustments(currentProject = {}) {
  return Array.isArray(currentProject.dueCompletionAdjustments)
    ? currentProject.dueCompletionAdjustments
    : [];
}

function buildTraceText(result, penaltyDays) {
  if (!result) return "";
  const lines = [
    "GCC 2015 Due Completion Date Calculation",
    `Base Due Completion Date: ${result.baseDueCompletionDate}`,
    `Current Due Completion Date: ${result.currentDueCompletionDate}`,
    `Total approved EOT days: ${result.totalApprovedEOTCalendarDays}`,
    `Total acceleration days: ${result.totalApprovedAccelerationCalendarDays}`,
    `Total saving-of-time days: ${result.totalApprovedSavingCalendarDays}`,
    `Penalty days: ${penaltyDays}`,
    "",
    "Calculation trace:",
  ];
  result.calculationTrace.forEach((item, index) => {
    lines.push(
      `${index + 1}. ${item.step}` +
        `${item.date ? ` (${item.date})` : ""}` +
        `${item.daysAdded !== undefined ? ` [${item.daysAdded} days]` : ""}` +
        ` - ${item.explanation}`
    );
  });
  return lines.join("\n");
}

function ToggleBox({ checked, children, title, onChange }) {
  return (
    <label
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 38,
        minHeight: 34,
        border: `1px solid ${checked ? t.color.accent : t.color.border}`,
        borderRadius: 8,
        background: checked ? t.color.accentSoft : t.color.surface,
        color: checked ? t.color.accent : t.color.ink,
        fontFamily: t.font.mono,
        fontSize: 12,
        fontWeight: 800,
        cursor: "pointer",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ display: "none" }}
      />
      {children}
    </label>
  );
}

function DueCompletionPanel({ currentProject, onSave }) {
  const [settings, setSettings] = useState(() => normaliseSettings(currentProject));
  const [adjustments, setAdjustments] = useState(() => normaliseAdjustments(currentProject));
  const [actualPracticalCompletionDate, setActualPracticalCompletionDate] = useState(
    currentProject?.actualPracticalCompletionDate || ""
  );
  const [publicHolidayText, setPublicHolidayText] = useState(() =>
    toLines(normaliseSettings(currentProject).publicHolidays)
  );
  const [saveState, setSaveState] = useState("idle");
  const [copyState, setCopyState] = useState("idle");

  useEffect(() => {
    const nextSettings = normaliseSettings(currentProject);
    setSettings(nextSettings);
    setAdjustments(normaliseAdjustments(currentProject));
    setActualPracticalCompletionDate(currentProject?.actualPracticalCompletionDate || "");
    setPublicHolidayText(toLines(nextSettings.publicHolidays));
  }, [currentProject]);

  const effectiveSettings = useMemo(() => ({
    ...settings,
    durationValue: numberOrZero(settings.durationValue),
    workingDaysPerWeek: numberOrZero(settings.workingDaysPerWeek) || 5,
    publicHolidays: fromLines(publicHolidayText),
    specialNonWorkingPeriods: settings.specialNonWorkingPeriods || [],
    sections: settings.sections || [],
  }), [settings, publicHolidayText]);

  const calculation = useMemo(() => {
    try {
      if (!effectiveSettings.commencementDate || !effectiveSettings.durationUnit || !effectiveSettings.calculationMode) {
        return { result: null, error: "Enter the commencement date, duration and calculation mode." };
      }
      return { result: calculateDueCompletion(effectiveSettings, adjustments), error: "" };
    } catch (error) {
      return { result: null, error: error?.message || "Due completion calculation failed." };
    }
  }, [effectiveSettings, adjustments]);

  const penaltyDays = useMemo(() => {
    if (!calculation.result || !actualPracticalCompletionDate) return 0;
    try {
      return calculateDelayPenaltyDays(
        calculation.result.currentDueCompletionDate,
        actualPracticalCompletionDate
      );
    } catch (_error) {
      return 0;
    }
  }, [actualPracticalCompletionDate, calculation.result]);

  const traceText = useMemo(
    () => buildTraceText(calculation.result, penaltyDays),
    [calculation.result, penaltyDays]
  );

  const patchSettings = (patch) => setSettings((prev) => ({ ...prev, ...patch }));

  const toggleWeekday = (value, checked) => {
    const existing = new Set((settings.nonWorkingWeekdays || []).map(Number));
    if (checked) existing.add(value);
    else existing.delete(value);
    patchSettings({ nonWorkingWeekdays: Array.from(existing).sort((a, b) => a - b) });
  };

  const addPeriod = () => {
    patchSettings({
      specialNonWorkingPeriods: [
        ...(settings.specialNonWorkingPeriods || []),
        { id: newId("special-period"), name: "Year-end break", startDate: "", endDate: "", inclusive: true },
      ],
    });
  };

  const updatePeriod = (index, patch) => {
    patchSettings({
      specialNonWorkingPeriods: (settings.specialNonWorkingPeriods || []).map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item
      ),
    });
  };

  const removePeriod = (index) => {
    patchSettings({
      specialNonWorkingPeriods: (settings.specialNonWorkingPeriods || []).filter((_, itemIndex) => itemIndex !== index),
    });
  };

  const addSection = () => {
    patchSettings({
      sections: [
        ...(settings.sections || []),
        { id: newId("section"), name: "Sectional completion", durationValue: 0, durationUnit: "weeks" },
      ],
    });
  };

  const updateSection = (index, patch) => {
    patchSettings({
      sections: (settings.sections || []).map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item
      ),
    });
  };

  const removeSection = (index) => {
    patchSettings({ sections: (settings.sections || []).filter((_, itemIndex) => itemIndex !== index) });
  };

  const addAdjustment = () => {
    setAdjustments((prev) => [
      ...prev,
      {
        id: newId("time-adjustment"),
        type: "extensionOfTime",
        title: "Approved time adjustment",
        approved: true,
        approvedDays: 0,
        dayType: "calendarDays",
        affectsCriticalPath: true,
        concurrentWithAdjustmentIds: [],
        notes: "",
      },
    ]);
  };

  const updateAdjustment = (index, patch) => {
    setAdjustments((prev) => prev.map((item, itemIndex) => (
      itemIndex === index ? { ...item, ...patch } : item
    )));
  };

  const removeAdjustment = (index) => {
    setAdjustments((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const save = async () => {
    if (!onSave) return;
    setSaveState("saving");
    await onSave({
      dueCompletionSettings: effectiveSettings,
      dueCompletionAdjustments: adjustments,
      actualPracticalCompletionDate,
      dueCompletionResult: calculation.result || null,
      delayPenaltyDays: penaltyDays,
    });
    setSaveState("saved");
    window.setTimeout(() => setSaveState("idle"), 1600);
  };

  const copyTrace = async () => {
    if (!traceText) return;
    try {
      await navigator.clipboard.writeText(traceText);
      setCopyState("copied");
    } catch (_error) {
      setCopyState("failed");
    }
    window.setTimeout(() => setCopyState("idle"), 1600);
  };

  return (
    <Card
      title="GCC Due Completion Date"
      subtitle="Standalone GCC 2015 due date calculator for contract-data durations, special non-working days, EOTs, acceleration and penalties."
      accent
      action={
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <Button size="sm" variant="outline" onClick={copyTrace} disabled={!calculation.result}>
            {copyState === "copied" ? "Copied" : copyState === "failed" ? "Copy Failed" : "Copy Trace"}
          </Button>
          <Button size="sm" variant="accent" onClick={save} disabled={!calculation.result || saveState === "saving"}>
            {saveState === "saving" ? "Saving" : saveState === "saved" ? "Saved" : "Save"}
          </Button>
        </div>
      }
    >
      <div style={{ padding: 20, display: "grid", gap: 18 }}>
        {calculation.error && (
          <div style={{
            border: `1px solid ${t.color.danger}`,
            background: t.color.dangerSoft,
            color: t.color.danger,
            borderRadius: 8,
            padding: "10px 12px",
            fontSize: 13,
            fontWeight: 700,
          }}>
            {calculation.error}
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(140px, 1fr))", gap: 12 }}>
          <Card padded={false} style={{ borderRadius: 8 }}>
            <div style={{ padding: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: t.color.inkSubtle }}>Base Due Date</div>
              <div style={{ marginTop: 8, fontFamily: t.font.mono, fontSize: 18, fontWeight: 800 }}>{calculation.result?.baseDueCompletionDate || "—"}</div>
            </div>
          </Card>
          <Card padded={false} style={{ borderRadius: 8 }}>
            <div style={{ padding: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: t.color.inkSubtle }}>Current Due Date</div>
              <div style={{ marginTop: 8, fontFamily: t.font.mono, fontSize: 18, fontWeight: 800 }}>{calculation.result?.currentDueCompletionDate || "—"}</div>
            </div>
          </Card>
          <Card padded={false} style={{ borderRadius: 8 }}>
            <div style={{ padding: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: t.color.inkSubtle }}>Approved EOT</div>
              <div style={{ marginTop: 8, fontFamily: t.font.mono, fontSize: 18, fontWeight: 800 }}>{calculation.result?.totalApprovedEOTCalendarDays || 0} days</div>
            </div>
          </Card>
          <Card padded={false} style={{ borderRadius: 8 }}>
            <div style={{ padding: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: t.color.inkSubtle }}>Penalty Days</div>
              <div style={{ marginTop: 8, fontFamily: t.font.mono, fontSize: 18, fontWeight: 800 }}>{penaltyDays}</div>
            </div>
          </Card>
        </div>

        <FormGroupDivider>Contract Data</FormGroupDivider>
        <FormRow label="Commencement Date" helper="GCC 2015 clause 1.1.1.14 basis date.">
          <Input
            type="date"
            value={settings.commencementDate || ""}
            onChange={(e) => patchSettings({ commencementDate: e.target.value })}
          />
        </FormRow>
        <FormRow label="Duration" helper="Weeks are interpreted according to the selected calculation mode.">
          <div style={{ display: "grid", gridTemplateColumns: "160px minmax(180px, 1fr)", gap: 10 }}>
            <Input
              type="number"
              min="0"
              step="1"
              value={settings.durationValue ?? ""}
              onChange={(e) => patchSettings({ durationValue: e.target.value })}
            />
            <Select
              value={settings.durationUnit}
              onChange={(e) => patchSettings({ durationUnit: e.target.value })}
            >
              <option value="calendarDays">Calendar days</option>
              <option value="workingDays">Working days</option>
              <option value="weeks">Weeks</option>
              <option value="months">Months</option>
            </Select>
          </div>
        </FormRow>
        <FormRow label="Calculation Mode" helper="GCC default uses calendar days; contract working-calendar skips configured non-working dates.">
          <Select
            value={settings.calculationMode}
            onChange={(e) => patchSettings({ calculationMode: e.target.value })}
          >
            <option value="gccCalendarDays">GCC calendar-day mode</option>
            <option value="contractWorkingCalendar">Contract working-calendar mode</option>
          </Select>
        </FormRow>
        <FormRow label="Count Commencement" helper="Default is off, so elapsed time starts the day after Commencement.">
          <ToggleBox
            checked={settings.countCommencementDate === true}
            onChange={(checked) => patchSettings({ countCommencementDate: checked })}
            title="Count commencement date as elapsed time"
          >
            Yes
          </ToggleBox>
        </FormRow>

        <FormGroupDivider>Contract Calendar</FormGroupDivider>
        <FormRow label="Working Days/Week" helper="Used when weeks are converted in contract working-calendar mode.">
          <Input
            type="number"
            min="1"
            step="1"
            value={settings.workingDaysPerWeek ?? 5}
            onChange={(e) => patchSettings({ workingDaysPerWeek: e.target.value })}
          />
        </FormRow>
        <FormRow label="Non-working Weekdays" helper="Only selected weekdays are skipped. Public holidays do not automatically skip weekends.">
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {WEEKDAYS.map((day) => (
              <ToggleBox
                key={day.value}
                title={day.title}
                checked={(settings.nonWorkingWeekdays || []).map(Number).includes(day.value)}
                onChange={(checked) => toggleWeekday(day.value, checked)}
              >
                {day.label}
              </ToggleBox>
            ))}
          </div>
        </FormRow>
        <FormRow label="Public Holidays" helper="One ISO date per line. These are project settings, not hardcoded inside the calculator.">
          <Textarea
            rows={5}
            value={publicHolidayText}
            placeholder="2025-09-24"
            onChange={(e) => setPublicHolidayText(e.target.value)}
            style={{ fontFamily: t.font.mono }}
          />
        </FormRow>
        <FormRow label="Special Non-working Periods" helper="Use for year-end breaks and other contractual special non-working periods.">
          <div style={{ display: "grid", gap: 10 }}>
            {(settings.specialNonWorkingPeriods || []).map((period, index) => (
              <div key={period.id || index} style={{ display: "grid", gridTemplateColumns: "1fr 150px 150px 90px", gap: 8 }}>
                <Input
                  value={period.name || ""}
                  placeholder="Year-end break"
                  onChange={(e) => updatePeriod(index, { name: e.target.value })}
                />
                <Input
                  type="date"
                  value={period.startDate || ""}
                  onChange={(e) => updatePeriod(index, { startDate: e.target.value })}
                />
                <Input
                  type="date"
                  value={period.endDate || ""}
                  onChange={(e) => updatePeriod(index, { endDate: e.target.value })}
                />
                <Button size="sm" variant="danger" onClick={() => removePeriod(index)}>Remove</Button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addPeriod}>Add Period</Button>
          </div>
        </FormRow>

        <FormGroupDivider>Sectional Completion</FormGroupDivider>
        <FormRow label="Sections" helper="Optional section-specific durations calculated from the same commencement date and calendar settings.">
          <div style={{ display: "grid", gap: 10 }}>
            {(settings.sections || []).map((section, index) => (
              <div key={section.id || index} style={{ display: "grid", gridTemplateColumns: "1fr 130px 160px 90px", gap: 8 }}>
                <Input
                  value={section.name || ""}
                  placeholder="Section name"
                  onChange={(e) => updateSection(index, { name: e.target.value })}
                />
                <Input
                  type="number"
                  min="0"
                  value={section.durationValue ?? ""}
                  onChange={(e) => updateSection(index, { durationValue: numberOrZero(e.target.value) })}
                />
                <Select
                  value={section.durationUnit || "weeks"}
                  onChange={(e) => updateSection(index, { durationUnit: e.target.value })}
                >
                  <option value="calendarDays">Calendar days</option>
                  <option value="workingDays">Working days</option>
                  <option value="weeks">Weeks</option>
                  <option value="months">Months</option>
                </Select>
                <Button size="sm" variant="danger" onClick={() => removeSection(index)}>Remove</Button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addSection}>Add Section</Button>
          </div>
        </FormRow>

        <FormGroupDivider>Time Adjustments</FormGroupDivider>
        <FormRow label="EOT / Acceleration / Saving" helper="Only approved critical-path adjustments affect the current Due Completion Date.">
          <div style={{ display: "grid", gap: 10 }}>
            {adjustments.map((item, index) => (
              <div key={item.id || index} style={{ border: `1px solid ${t.color.border}`, borderRadius: 8, padding: 10, display: "grid", gap: 8 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 160px 120px 150px 90px", gap: 8 }}>
                  <Input
                    value={item.title || ""}
                    placeholder="Adjustment title"
                    onChange={(e) => updateAdjustment(index, { title: e.target.value })}
                  />
                  <Select value={item.type} onChange={(e) => updateAdjustment(index, { type: e.target.value })}>
                    <option value="extensionOfTime">EOT</option>
                    <option value="acceleration">Acceleration</option>
                    <option value="savingOfTime">Saving of time</option>
                  </Select>
                  <Input
                    type="number"
                    min="0"
                    value={item.approvedDays ?? 0}
                    onChange={(e) => updateAdjustment(index, { approvedDays: numberOrZero(e.target.value) })}
                  />
                  <Select value={item.dayType} onChange={(e) => updateAdjustment(index, { dayType: e.target.value })}>
                    <option value="calendarDays">Calendar days</option>
                    <option value="workingDays">Working days</option>
                  </Select>
                  <Button size="sm" variant="danger" onClick={() => removeAdjustment(index)}>Remove</Button>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <ToggleBox checked={item.approved === true} title="Approved" onChange={(checked) => updateAdjustment(index, { approved: checked })}>Approved</ToggleBox>
                  <ToggleBox checked={item.affectsCriticalPath === true} title="Affects critical path" onChange={(checked) => updateAdjustment(index, { affectsCriticalPath: checked })}>Critical</ToggleBox>
                  <Input
                    value={(item.concurrentWithAdjustmentIds || []).join(", ")}
                    placeholder="Concurrent with adjustment IDs"
                    onChange={(e) => updateAdjustment(index, {
                      concurrentWithAdjustmentIds: e.target.value.split(",").map((value) => value.trim()).filter(Boolean),
                    })}
                    style={{ flex: "1 1 240px" }}
                  />
                </div>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addAdjustment}>Add Adjustment</Button>
          </div>
        </FormRow>

        <FormRow label="Actual Practical Completion" helper="Penalty days count every calendar day after the current Due Completion Date.">
          <Input
            type="date"
            value={actualPracticalCompletionDate || ""}
            onChange={(e) => setActualPracticalCompletionDate(e.target.value)}
          />
        </FormRow>

        {calculation.result?.sections?.length > 0 && (
          <>
            <FormGroupDivider>Sectional Dates</FormGroupDivider>
            <Table>
              <thead>
                <tr>
                  <Th>Section</Th>
                  <Th width={170}>Base Due Date</Th>
                  <Th width={170}>Current Due Date</Th>
                </tr>
              </thead>
              <tbody>
                {calculation.result.sections.map((section) => (
                  <tr key={section.id}>
                    <Td weight={700}>{section.name}</Td>
                    <Td mono>{section.baseDueCompletionDate}</Td>
                    <Td mono>{section.currentDueCompletionDate}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}

        <FormGroupDivider>Calculation Trace</FormGroupDivider>
        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Badge variant="primary">EOT {calculation.result?.totalApprovedEOTCalendarDays || 0} days</Badge>
            <Badge variant="warning">Acceleration {calculation.result?.totalApprovedAccelerationCalendarDays || 0} days</Badge>
            <Badge variant="default">Saving {calculation.result?.totalApprovedSavingCalendarDays || 0} days</Badge>
          </div>
          <Textarea value={traceText} readOnly rows={10} style={{ fontFamily: t.font.mono }} />
        </div>
      </div>
    </Card>
  );
}

export default memo(DueCompletionPanel);
